import { Autocomplete } from '@mui/material';
import { FC, SetStateAction, useCallback, useMemo } from 'react';

import { ExpirationDate, SearchableSelect, SearchableSelectOption, StyledTextField } from 'components';
import { TextField } from 'components/inputs/text-field';
import { useLookups, useSizeTypes } from 'contexts';
import { Booking, Chassis, Customer, EquipmentType, Genset, UnitStatus } from 'types';

import { ChassisStack, GensetStack } from '.';
import { useNewTransaction } from '..';
import { AccountSearch } from '../account';
import { BookingInput } from '../booking';
import { InboundUnitSearch, OutboundUnitSearch } from '../equipment';
import ManufacturedDate from '../equipment/manufactured-date';
import './stack.scss';

const isChassisStack = (stack: Chassis[] | Genset[]): stack is Chassis[] =>
  !stack.length || stack[0] instanceof Chassis;

const isGensetStack = (stack: Chassis[] | Genset[]): stack is Genset[] => !stack.length || stack[0] instanceof Genset;

const StackInformation: FC = () => {
  const { chassis, stack, transaction, unstack, setChassis, setStack, setUnstack } = useNewTransaction();
  const { unitStatusOptions } = useLookups();
  const { chassisSizeTypeEntries } = useSizeTypes();

  const sizeTypeOptions: SearchableSelectOption[] = useMemo(() => {
    return chassisSizeTypeEntries.filter(e => e[1].length === 1).map(e => ({ label: e[0], value: e[1][0] }));
  }, [chassisSizeTypeEntries]);

  const enableAddUnit = useMemo(() => {
    /* outbound units must be in yard, inbound units must not be blank */
    return [...stack].every((unit: Chassis | Genset) => (!transaction.inbound ? unit.id > 0 : unit.unitNumber));
  }, [stack, transaction.inbound]);

  /* memoize customer change handler to avoid account search rerender */
  const setCustomer = useCallback(
    (customer?: Customer | null) => {
      setChassis(p => new Chassis({ ...p, customer }));
    },
    [setChassis]
  );

  const setBooking = useCallback(
    (args: SetStateAction<Partial<Booking> | undefined | null>) => {
      setChassis(c => new Chassis({ ...c, booking: typeof args === 'function' ? args(c.booking) : args }));
    },
    [setChassis]
  );

  const setBaseChassis = useCallback(
    (args?: SetStateAction<Chassis>) => {
      setChassis(c => new Chassis(typeof args === 'function' ? args(c) : args));
    },
    [setChassis]
  );

  const addChassis = useCallback(() => {
    setStack(readOnlyStack => {
      if (!isChassisStack(readOnlyStack)) return readOnlyStack;
      const template: Chassis = !readOnlyStack?.length ? chassis : readOnlyStack[readOnlyStack.length - 1];
      const { sizeType, customer, unitStatus, booking } = { ...template };
      return [...readOnlyStack, new Chassis({ sizeType, customer, unitStatus, booking, id: -new Date().getTime() })];
    });
  }, [chassis, setStack]);

  const addGenset = useCallback(() => {
    setStack(readOnlyStack => {
      if (!isGensetStack(readOnlyStack)) return readOnlyStack;
      const template: Chassis | Genset = !readOnlyStack?.length ? chassis : readOnlyStack[readOnlyStack.length - 1];
      let { sizeType, customer, unitStatus, carbCompliant, booking } = new Genset({ ...template });

      if (!booking?.bookingDetails?.some(({ iso }) => iso && (iso.includes('00GS') || iso.includes('00GU')))) {
        booking = undefined;
      }

      if (sizeType !== '00GS' && sizeType !== '00GU') {
        sizeType = undefined;
      }

      return [
        ...readOnlyStack,
        new Genset({ sizeType, customer, unitStatus, carbCompliant, booking, id: -new Date().getTime() })
      ];
    });
  }, [chassis, setStack]);

  const validateOutboundBaseChassis = useCallback(
    async (unit: Chassis | null) => {
      if (!unit) {
        /* reset previously mounted units in stack */
        setStack([]);
        return;
      }
      if (unit.baseUnit) {
        return `Invalid base chassis. ${unit.unitNumber} mounted on ${unit.baseUnit.unitNumber}`;
      }
      const mountedContainer = unit.mountedUnits?.find(mu => mu.equipmentType === EquipmentType.CONTAINER);
      if (mountedContainer) {
        return `Invalid bulk transaction. ${mountedContainer.unitNumber} (${EquipmentType.CONTAINER}) \
              already mounted on ${unit.unitNumber}`;
      }
      const mountedGensets = unit.mountedUnits?.filter(mu => mu.equipmentType === EquipmentType.GENSET);
      const mountedChasses = unit.mountedUnits?.filter(mu => mu.equipmentType === EquipmentType.CHASSIS);
      if (mountedGensets?.length) {
        setStack(mountedGensets.map(mg => new Genset(mg)));
      } else if (mountedChasses?.length) {
        setStack(mountedChasses.map(mc => new Chassis(mc)));
      }
    },
    [setStack]
  );

  return (
    <section id="equipment-stack" className="d-flex flex-column g-3">
      <div className="grid-3">
        <div className="col-start-1">
          {chassis.flatbed || chassis.ownerChassis ? (
            <TextField
              label="UNIT NUMBER"
              disabled={!!chassis.flatbed}
              value={chassis.unitNumber ?? ''}
              onChange={unitNumber => setBaseChassis(u => ({ ...u, unitNumber }))}
              data-cy="base-chassis-private-unit-number-input"
            />
          ) : transaction.inbound ? (
            <InboundUnitSearch
              equipmentType={EquipmentType.CHASSIS}
              required={!chassis.flatbed && !chassis.ownerChassis}
              unit={chassis}
              setUnit={setBaseChassis}
              data-cy="base-chassis-inbound-unit-number-search"
            />
          ) : (
            <OutboundUnitSearch
              unit={chassis}
              setUnit={setBaseChassis}
              required={!chassis.flatbed && !chassis.ownerChassis}
              customValidation={validateOutboundBaseChassis}
              data-cy="base-chassis-outbound-unit-number-search"
            />
          )}
        </div>
        <div className="col-start-2">
          <SearchableSelect
            containerClass="text-field"
            inputClass={!chassis.sizeType ? 'blank' : ''}
            id="chassis-size-type"
            label="SIZE TYPE"
            disabled={!transaction.inbound && !chassis.flatbed && !chassis.ownerChassis}
            required={!chassis.flatbed && !chassis.ownerChassis}
            onClear={() => setBaseChassis(p => ({ ...p, sizeType: undefined }))}
            onValueSelected={(sizeType: any) => setBaseChassis(p => p && { ...p, sizeType })}
            options={sizeTypeOptions}
            value={chassis.sizeType ?? ''}
            data-cy="base-chassis-size-type-input"
          />
        </div>
        <div className="col-start-3">
          <AccountSearch
            required={!chassis.flatbed && !chassis.ownerChassis}
            unit={chassis}
            setCustomer={setCustomer}
            data-cy="base-chassis-account-search"
          />
        </div>
        <div className="col-start-1 row-start-2">
          <Autocomplete<UnitStatus | ''>
            fullWidth
            autoHighlight
            size="small"
            disabled={!transaction.inbound && !chassis.flatbed && !chassis.ownerChassis}
            options={['', ...unitStatusOptions]}
            value={chassis.unitStatus ?? ''}
            onChange={(_, status) => setBaseChassis(c => ({ ...c, unitStatus: status || undefined }))}
            getOptionLabel={status => status ?? ''}
            renderInput={params => (
              <StyledTextField
                {...params}
                required={!chassis.flatbed && !chassis.ownerChassis}
                label="STATUS"
                data-cy="base-chassis-unit-status-input"
              />
            )}
            renderOption={(props, status) => (
              <li {...props} key={status ?? ''}>
                {status ?? ''}
              </li>
            )}
          />
        </div>
        <div className="col-start-2 row-start-2">
          <StyledTextField
            fullWidth
            label="LICENSE PLATE"
            value={chassis.licensePlate ?? ''}
            onChange={e => setBaseChassis(c => ({ ...c, licensePlate: e.target.value.toUpperCase() }))}
            disabled={!transaction.inbound && !chassis.flatbed && !chassis.ownerChassis}
            data-cy="base-chassis-license-plate-input"
          />
        </div>
        <ExpirationDate
          label="LP EXP"
          date={chassis.licensePlateExpirationDate}
          setDate={licensePlateExpirationDate => setBaseChassis(c => ({ ...c, licensePlateExpirationDate }))}
          disabled={!transaction.inbound && !chassis.flatbed && !chassis.ownerChassis}
          data-cy="base-chassis-license-plate-expiration-date-input"
        />
        <ExpirationDate
          label="FMCSA"
          date={chassis.fmcsaDate}
          setDate={fmcsaDate => setBaseChassis(c => ({ ...c, fmcsaDate }))}
          disabled={!transaction.inbound && !chassis.flatbed && !chassis.ownerChassis}
          data-cy="base-chassis-fmcsa-date-input"
        />
        <BookingInput
          unit={chassis}
          setBooking={setBooking}
          required={!transaction.inbound && !chassis.flatbed && !chassis.ownerChassis}
          disabled={!!chassis.flatbed || !!chassis.ownerChassis}
          data-cy="base-chassis-booking-search"
        />
        <ManufacturedDate unit={chassis} setUnit={setBaseChassis} />
        <div className="checkboxes col-start-3 d-flex g-2 h-100">
          <div className="flex-fill d-flex g-2 h-100 align-items-center justify-content-center">
            <label className="font-weight-bold white-space-nowrap">FLTBD / PVT</label>
            <input
              type="checkbox"
              checked={!!chassis.flatbed}
              disabled={!!chassis.ownerChassis}
              onChange={e =>
                setChassis(
                  c =>
                    new Chassis({
                      id: null as any,
                      unitNumber: !e.target.checked ? '' : 'FLTBDPVT',
                      customer: c.customer,
                      sizeType: c.sizeType,
                      flatbed: !!e.target.checked
                    })
                )
              }
              data-cy="base-chassis-private-checkbox"
            />
          </div>
          <div className="flex-fill d-flex g-2 h-100 align-items-center justify-content-center">
            <label className="font-weight-bold">KEEPING</label>
            <input
              type="checkbox"
              checked={!!chassis.ownerChassis}
              disabled={!!chassis.flatbed}
              onChange={e =>
                setChassis(
                  c =>
                    new Chassis({
                      id: null as any,
                      unitNumber: c.unitNumber,
                      customer: c.customer,
                      sizeType: c.sizeType,
                      ownerChassis: !!e.target.checked
                    })
                )
              }
              data-cy="base-chassis-owner-checkbox"
            />
          </div>
        </div>
      </div>
      <ChassisStack />
      <GensetStack />
      <div className="grid-6">
        <button
          className="add btn-outline d-flex h-100 align-items-center p-0"
          type="button"
          onClick={addChassis}
          disabled={!isChassisStack(stack) || stack.length >= 9 || !enableAddUnit}
          data-cy="add-mounted-chassis-button"
        >
          <span className="material-icons">add</span>
          ADD CHASSIS
        </button>
        <button
          className="add btn-outline d-flex h-100 align-items-center p-0"
          type="button"
          onClick={addGenset}
          disabled={!isGensetStack(stack) || !enableAddUnit}
          data-cy="add-mounted-genset-button"
        >
          <span className="material-icons">add</span>
          ADD GENSET
        </button>
        {transaction.inbound && (
          <div className="d-flex g-2 h-100 align-items-center">
            <label className="font-weight-bold text-base">UNSTACK</label>
            <input
              type="checkbox"
              checked={unstack}
              disabled={isGensetStack(stack)}
              onChange={() => setUnstack(true)}
            />
            <label className="font-weight-bold margin-right-8px">YES</label>
            <input
              type="checkbox"
              checked={!unstack}
              disabled={isGensetStack(stack)}
              onChange={() => setUnstack(false)}
            />
            <label className="font-weight-bold margin-right-8px">NO</label>
          </div>
        )}
      </div>
    </section>
  );
};

export default StackInformation;
